import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  makeStyles,
  shorthands,
  tokens,
  Button,
  Text,
  Field,
  Input,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToolbarButton,
  Tooltip,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId,
} from '@fluentui/react-components';
import { Add24Regular, Stack24Regular, Search20Regular, PanelLeft24Regular, Delete20Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import TablePage from '../components/TablePage';
import ProjectStatusBadge from '../components/ProjectStatusBadge';
import ProjectFiltersSidebar from '../components/ProjectFiltersSidebar';
import { useProjectFilters } from '../hooks/useProjectFilters';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { CustomerSearchInput } from '../components/CustomerSearchInput';
import { getRowMenuForRoute } from '../config/toolbars';
import { queryKeys } from '../lib/queryClient';
import { fetchProjects, createProject, updateProject, Project, ProjectStatus } from '../services/projectService';
import { formatBusinessDate } from '../lib/businessTime';

const money = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

// Flat row shape for the table (DataTable sorts/filters on these scalar fields).
interface ProjectRow {
  id: string;
  number: string;
  name: string;
  customer: string;
  status: ProjectStatus;
  phases: number;
  total: string;
  totalRaw: number;
  collected: string;
  collectedRaw: number;
  created: string;
  /** Creation time in ms epoch (0 when unknown) — drives the filter date range. */
  createdAtRaw: number;
}

const useStyles = makeStyles({
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: '12px',
    ...shorthands.padding('64px', '24px'),
    textAlign: 'center',
  },
  emptyIcon: {
    color: tokens.colorNeutralForeground3,
  },
  emptySubtext: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '420px',
  },
  dialogField: {
    marginBottom: '16px',
  },
});

const Projects: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);

  const { data: projects, isLoading, isError } = useQuery<Project[]>({
    queryKey: queryKeys.projects.all,
    queryFn: fetchProjects,
  });

  // Controlled row selection — drives the checkbox column, select-all, and the
  // bulk-action bar (mirrors Orders/Customers).
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  // --- Delete (archive) a project from the row context menu ---------------
  // "Delete" archives server-side (PUT { archived: true }); list_projects only
  // returns non-archived rows, so it drops out on refetch. The backend REFUSES
  // (409) to archive a project while any phase is still reserved/started — live
  // equipment is committed. We deliberately do NOT optimistically remove the
  // row: an optimistic drop would flash it out and the rollback would silently
  // boomerang it back. Instead the row stays put and the 409's message is
  // surfaced so staff know to tear the live phases down first.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (projectId: string) => updateProject(projectId, { archived: true }),
    onError: (err: any) => {
      dispatchToast(
        <Toast>
          <ToastTitle>Couldn't delete project</ToastTitle>
          <ToastBody>{err?.message || 'Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' },
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.projects.all }),
  });

  // --- Bulk delete (archive) the selected projects ------------------------
  // No bulk endpoint exists, so fan out one archive PUT per id and settle them
  // all. Same non-optimistic stance as the single delete: rows that the backend
  // refuses (409 — live phases) stay put, and we surface how many were skipped
  // so staff know to tear those down first. Refetch on settle reconciles.
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const bulkDeleteMutation = useMutation({
    mutationFn: async (projectIds: string[]) => {
      const settled = await Promise.allSettled(
        projectIds.map((id) => updateProject(id, { archived: true })),
      );
      return settled.map((r, i) => ({
        id: projectIds[i],
        ok: r.status === 'fulfilled',
        error: r.status === 'rejected' ? (r.reason as Error)?.message : undefined,
      }));
    },
    onSuccess: (results) => {
      const failed = results.filter((r) => !r.ok);
      const ok = results.length - failed.length;
      if (failed.length > 0) {
        dispatchToast(
          <Toast>
            <ToastTitle>
              {`Deleted ${ok}, skipped ${failed.length} ${failed.length === 1 ? 'project' : 'projects'}`}
            </ToastTitle>
            <ToastBody>
              {failed[0].error
                ? `${failed[0].error}${failed.length > 1 ? ' (and others)' : ''}`
                : 'Tear down any live phases first, then try again.'}
            </ToastBody>
          </Toast>,
          { intent: 'warning' },
        );
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.projects.all }),
  });

  useEffect(() => {
    const handleDeleteProject = (e: CustomEvent<ProjectRow>) => {
      const row = e.detail;
      setDeleteTarget({ id: row.id, label: row.name !== '—' ? row.name : row.number });
    };
    window.addEventListener('deleteProject', handleDeleteProject as EventListener);
    return () => window.removeEventListener('deleteProject', handleDeleteProject as EventListener);
  }, []);

  const rowMenuItems = getRowMenuForRoute(location.pathname);

  // --- New-project dialog -------------------------------------------------
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<{ id: string; name: string } | null>(null);
  const [projectName, setProjectName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const resetDialog = () => {
    setSelectedCustomer(null);
    setProjectName('');
    setCreateError(null);
  };

  const handleCreate = async () => {
    if (!selectedCustomer || !projectName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const { project_id } = await createProject({
        customer_id: selectedCustomer.id,
        name: projectName.trim(),
      });
      setDialogOpen(false);
      resetDialog();
      navigate(`/projects/${project_id}`);
    } catch (e: any) {
      setCreateError(e?.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const rows: ProjectRow[] = useMemo(
    () =>
      (projects || []).map((p) => {
        const totalRaw = p.phases.reduce((sum, ph) => sum + (ph.price || 0), 0);
        const collectedRaw = p.phases.reduce((sum, ph) => sum + (ph.paidAmount || 0), 0);
        return {
          id: p.id,
          number: `P-${p.projectNumber}`,
          name: p.name || '—',
          customer: p.customerName || '—',
          status: p.status,
          phases: p.phases.length,
          totalRaw,
          total: money(totalRaw),
          collectedRaw,
          collected: money(collectedRaw),
          created: p.createdAt ? formatBusinessDate(p.createdAt) : '',
          createdAtRaw: p.createdAt ? new Date(p.createdAt).getTime() : 0,
        };
      }),
    [projects],
  );

  // Sidebar filters (status, created date range). Live-applied; state persists.
  const filters = useProjectFilters();
  const filterPanelCollapsed = filters.state.sidebarCollapsed;
  const toggleFilterPanel = () =>
    filters.setField('sidebarCollapsed', !filters.state.sidebarCollapsed);
  const filteredRows = useMemo(() => filters.applyFilters(rows), [rows, filters]);

  // Live search across the table, on top of the sidebar-filtered set. Project
  // number, name, customer, and status are all substring matches.
  const [tableSearch, setTableSearch] = useState('');
  const searchedRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return filteredRows;
    return filteredRows.filter((r) =>
      r.number.toLowerCase().includes(q)
      || r.name.toLowerCase().includes(q)
      || r.customer.toLowerCase().includes(q)
      || r.status.toLowerCase().includes(q),
    );
  }, [filteredRows, tableSearch]);

  const columns: ColumnConfig<ProjectRow>[] = [
    { columnKey: 'checkbox', label: '', sortable: false, groupable: false },
    { columnKey: 'number', label: 'Project', sortable: true, width: '120px' },
    { columnKey: 'name', label: 'Name', sortable: true },
    { columnKey: 'customer', label: 'Customer', sortable: true },
    {
      columnKey: 'status',
      label: 'Status',
      sortable: true,
      groupable: true,
      width: '130px',
      render: (row) => <ProjectStatusBadge status={row.status} />,
    },
    { columnKey: 'phases', label: 'Phases', sortable: true, width: '90px' },
    { columnKey: 'total', label: 'Total', sortable: true, width: '120px' },
    { columnKey: 'collected', label: 'Collected', sortable: true, width: '120px' },
    { columnKey: 'created', label: 'Created', sortable: true, width: '120px' },
  ];

  const newProjectButton = (
    <Button appearance="primary" icon={<Add24Regular />} onClick={() => setDialogOpen(true)}>
      New project
    </Button>
  );

  const noProjectsYet = !isLoading && !isError && rows.length === 0;

  return (
    <>
      <TablePage
        title="Projects"
        loading={isLoading}
        loadingLabel="Loading projects…"
        error={isError ? 'Couldn’t load projects.' : null}
        headerLeftActions={
          <Tooltip
            content={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            relationship="label"
          >
            <ToolbarButton
              icon={<PanelLeft24Regular />}
              onClick={toggleFilterPanel}
              aria-label={filterPanelCollapsed ? 'Show filters' : 'Hide filters'}
            />
          </Tooltip>
        }
        headerActions={newProjectButton}
        sidebar={
          <ProjectFiltersSidebar filters={filters} rows={rows} collapsed={filterPanelCollapsed} />
        }
        searchRow={
          noProjectsYet ? undefined : (
            <>
              <Input
                size="large"
                contentBefore={<Search20Regular />}
                placeholder="Filter projects by #, name, customer, status…"
                value={tableSearch}
                onChange={(_e, data) => setTableSearch(data.value)}
                style={{ flexGrow: 1, maxWidth: '480px' }}
              />
              {tableSearch.trim() && selectedProjectIds.length === 0 && (
                <span
                  style={{
                    fontSize: '13px',
                    color: tokens.colorNeutralForeground3,
                    marginLeft: 'auto',
                  }}
                >
                  {searchedRows.length} of {filteredRows.length}
                </span>
              )}
            </>
          )
        }
        selectedCount={selectedProjectIds.length}
        onClearSelection={() => setSelectedProjectIds([])}
        bulkNoun={{ singular: 'project', plural: 'projects' }}
        bulkActions={
          noProjectsYet
            ? undefined
            : [
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: <Delete20Regular />,
                  onClick: () => setBulkDeleteOpen(true),
                },
              ]
        }
      >
        {noProjectsYet ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Stack24Regular />
            </div>
            <Text weight="semibold" size={400}>
              No projects yet
            </Text>
            <Text className={styles.emptySubtext}>
              A project is a multi-phase rolloff job — a single site with repeated bin swap-outs,
              each invoiced separately. Use “New project” above to sequence the swaps.
            </Text>
          </div>
        ) : searchedRows.length > 0 ? (
          <DataTable
            data={searchedRows}
            columns={columns}
            keyField="id"
            hideableColumns
            columnVisibilityStorageKey="projects"
            selectedKeys={selectedProjectIds}
            onSelectionChange={setSelectedProjectIds}
            showCheckboxes
            itemsPerPage={25}
            primaryColumnKey="number"
            defaultSortColumn="created"
            defaultSortDirection="descending"
            onRowClick={(row) => navigate(`/projects/${row.id}`)}
            rowMenuItems={rowMenuItems || undefined}
            disableWrapper
          />
        ) : (
          // Projects exist, but the active filters/search hid them all —
          // offer a one-click reset rather than a dead end.
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <Search20Regular />
            </div>
            <Text weight="semibold" size={400}>
              No matching projects
            </Text>
            <Text className={styles.emptySubtext}>
              No projects match your current filters and search. Try broadening them to see more.
            </Text>
            <Button
              appearance="secondary"
              onClick={() => {
                filters.clearAll();
                setTableSearch('');
              }}
            >
              Clear filters &amp; search
            </Button>
          </div>
        )}
      </TablePage>

      <Dialog
        open={dialogOpen}
        onOpenChange={(_e, data) => {
          setDialogOpen(data.open);
          if (!data.open) resetDialog();
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>New project</DialogTitle>
            <DialogContent>
              <Field label="Customer" required className={styles.dialogField}>
                <CustomerSearchInput
                  selectedCustomer={selectedCustomer as any}
                  onCustomerSelect={(c) => setSelectedCustomer({ id: c.id, name: c.name })}
                  onCustomerClear={() => setSelectedCustomer(null)}
                  lazyLoad
                />
              </Field>
              <Field label="Project name" required className={styles.dialogField}>
                <Input
                  value={projectName}
                  onChange={(_e, data) => setProjectName(data.value)}
                  onKeyDown={(e) => {
                    // Enter submits, mirroring the Create button's enabled state.
                    if (e.key === 'Enter' && selectedCustomer && projectName.trim() && !creating) {
                      e.preventDefault();
                      handleCreate();
                    }
                  }}
                />
              </Field>
              {createError && (
                <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{createError}</Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDialogOpen(false)} disabled={creating}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleCreate}
                disabled={!selectedCustomer || !projectName.trim() || creating}
              >
                {creating ? 'Creating…' : 'Create project'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
        title="Delete project"
        message={`Are you sure you want to delete ${deleteTarget?.label ?? 'this project'}?`}
        subtext="This cannot be undone"
        confirmButtonText="Delete"
        variant="danger"
      />

      <ConfirmationDialog
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => {
          bulkDeleteMutation.mutate(selectedProjectIds);
          setBulkDeleteOpen(false);
          setSelectedProjectIds([]);
        }}
        title={`Delete ${selectedProjectIds.length} ${selectedProjectIds.length === 1 ? 'project' : 'projects'}`}
        message={`Are you sure you want to delete ${selectedProjectIds.length} ${selectedProjectIds.length === 1 ? 'project' : 'projects'}?`}
        subtext="This cannot be undone"
        confirmButtonText="Delete"
        variant="danger"
      />

      <Toaster toasterId={toasterId} />
    </>
  );
};

export default Projects;
